import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pick } from "@/components/vi/SaleDialog";
import { QuickForm } from "@/components/vi/ViDialogs";
import { Plus, Trash2 } from "lucide-react";
import { call, money } from "@/lib/vi";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";
import { cachedGet, runOp } from "@/lib/offline";

async function offlineCall(type, tourId, payload, label, okMsg, extra) {
  try {
    const r = await runOp(type, tourId, payload, label, extra);
    toast.success(r.queued ? `${okMsg} (hors ligne, synchronisation automatique)` : okMsg);
    return true;
  } catch (e) { toast.error(apiErr(e)); return false; }
}

// Commande entrepôt ou devis terrain : articles du catalogue complet (pas seulement le stock du tricycle)
export function LinesDialog({ tour, kind, open, onOpenChange, onDone }) {
  const [clients, setClients] = useState([]);
  const [arts, setArts] = useState([]);
  const [f, setF] = useState({});
  const [lines, setLines] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF({ urgence: "Normale", validite_jours: 15 }); setLines([{ article_id: "", quantite: 1 }]);
    cachedGet("/itinerant/clients").then(setClients).catch(() => {});
    cachedGet("/articles").then(setArts).catch(() => {});
  }, [open]);
  const upd = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const total = lines.reduce((s, l) => s + (arts.find((a) => a.id === l.article_id)?.prix_vente || 0) * l.quantite, 0);
  const isOrder = kind === "order";
  const submit = async () => {
    const body = { ...f, lines: lines.filter((l) => l.article_id) };
    const r = isOrder ? await call(() => api.post(`/itinerant/tournees/${tour.id}/orders`, body), "Commande transmise à l'entrepôt")
      : await offlineCall("quote", tour.id, body, clients.find((c) => c.id === f.client_id)?.nom || "", "Devis créé", { total });
    if (r) { onOpenChange(false); onDone(); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle className="font-head">{isOrder ? "Commande entrepôt" : "Devis terrain"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Client</Label><Pick value={f.client_id} onChange={(v) => setF({ ...f, client_id: v })} placeholder="Choisir" testid={`${kind}-client`} options={clients.map((c) => ({ value: c.id, label: c.nom }))} /></div>
          {lines.map((l, i) => {
            const a = arts.find((x) => x.id === l.article_id);
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`${kind}-line-${i}`}>
                <div className="col-span-12 sm:col-span-7"><Pick value={l.article_id} onChange={(v) => upd(i, "article_id", v)} placeholder="Article" testid={`${kind}-article-${i}`}
                  options={arts.map((x) => ({ value: x.id, label: `${x.designation} — ${money(x.prix_vente)}` }))} /></div>
                <div className="col-span-5 sm:col-span-2"><Input type="number" value={l.quantite} onChange={(e) => upd(i, "quantite", Number(e.target.value))} data-testid={`${kind}-qty-${i}`} /></div>
                <div className="col-span-5 sm:col-span-2 text-xs font-mono pb-2">{a ? money(a.prix_vente * l.quantite) : ""}</div>
                <button className="col-span-1 pb-2 text-slate-400" onClick={() => setLines(lines.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
          <Button size="sm" variant="outline" onClick={() => setLines([...lines, { article_id: "", quantite: 1 }])} data-testid={`${kind}-add-line`}><Plus className="h-4 w-4 mr-1" />Article</Button>
          {isOrder ? <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Date souhaitée</Label><Input type="date" className="mt-1" value={f.date_souhaitee || ""} onChange={(e) => setF({ ...f, date_souhaitee: e.target.value })} data-testid="order-date" /></div>
            <div><Label className="text-xs">Urgence</Label><Pick value={f.urgence} onChange={(v) => setF({ ...f, urgence: v })} testid="order-urgence" options={["Normale", "Urgente", "Très urgente"].map((u) => ({ value: u, label: u }))} /></div>
            <div className="col-span-2"><Label className="text-xs">Commentaire</Label><Input className="mt-1" value={f.commentaire || ""} onChange={(e) => setF({ ...f, commentaire: e.target.value })} /></div>
          </div> : <div><Label className="text-xs">Validité (jours)</Label><Input type="number" className="mt-1" value={f.validite_jours} onChange={(e) => setF({ ...f, validite_jours: Number(e.target.value) })} /></div>}
          <p className="text-sm text-right">Montant : <b className="font-mono" data-testid={`${kind}-total`}>{money(total)}</b></p>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={!f.client_id || !total} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid={`${kind}-submit`}>Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CheckinDialog({ tour, open, onOpenChange, onDone, presetCible }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!open) return;
    Promise.all([cachedGet("/itinerant/clients").catch(() => []), cachedGet("/prospects").catch(() => [])]).then(([c, p]) => setOpts([
      ...c.map((x) => ({ value: `c:${x.id}`, label: `Client · ${x.nom}` })),
      ...p.filter((x) => !x.client_id).map((x) => ({ value: `p:${x.id}`, label: `Prospect · ${x.entreprise || x.nom}` })),
    ]));
  }, [open]);
  const submit = async (f) => {
    if (!f.cible) return false;
    const [k, id] = f.cible.split(":");
    const pos = await new Promise((res) => {
      if (!navigator.geolocation) return res(null);
      navigator.geolocation.getCurrentPosition((p) => res(p.coords), () => res(null), { timeout: 5000 });
    });
    const label = (opts.find((o) => o.value === f.cible)?.label || "").replace(/^(Client|Prospect) · /, "");
    const r = await offlineCall("checkin", tour.id, { [k === "c" ? "client_id" : "prospect_id"]: id, lat: pos?.latitude, lng: pos?.longitude }, label, "Check-in enregistré");
    if (r) onDone(); return r;
  };
  return <QuickForm open={open} onOpenChange={onOpenChange} title="CHECK-IN — début de visite" testid="checkin-dialog" submitLabel="CHECK-IN" initial={presetCible ? { cible: presetCible } : {}}
    fields={[{ name: "cible", label: "Boutique visitée", type: "select", options: opts }]} onSubmit={submit} />;
}

export function CheckoutDialog({ visit, open, onOpenChange, onDone }) {
  return <QuickForm open={open} onOpenChange={onOpenChange} title={`CHECK-OUT — ${visit?.cible || ""}`} testid="checkout-dialog" submitLabel="CHECK-OUT" initial={{ resultat: "Vente" }}
    fields={[{ name: "resultat", label: "Résultat de la visite", type: "select", options: ["Vente", "Commande", "Devis", "Prospection", "Relance", "Aucun besoin", "Client absent", "Réclamation", "Autre"] },
      { name: "commentaire", label: "Commentaire", type: "textarea" }]}
    onSubmit={async (f) => {
      const local = visit.id.startsWith("local:");
      const r = await offlineCall("checkout", visit.tournee_id || "", { ...f, ...(local ? { visit_op_id: visit.id.slice(6) } : { visit_id: visit.id }) }, visit.cible, "Visite terminée");
      if (r) onDone(); return r;
    }} />;
}
